// Cohort + LTV — análisis de retención por mes de alta + valor de vida del cliente.
// Cohorte = clientes que se suscribieron en el mismo mes.
// Para cada cohorte, mostramos cuántos siguen activos a M+1, M+3, M+6.
// LTV = total cobrado / cliente.

import { useEffect, useMemo, useState } from 'react'
import AdminLayout from '../../components/AdminLayout'
import AnalisisIA from '../../components/AnalisisIA'
import { supabase } from '../../lib/supabase'

const fmtUYU = (n) => `$${Math.round(Number(n || 0)).toLocaleString('es-UY')}`
const ymKey = (d) => {
  const dt = new Date(d)
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`
}
const ymLabel = (ym) => {
  const [y, m] = ym.split('-')
  const meses = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Set','Oct','Nov','Dic']
  return `${meses[Number(m) - 1]} ${y.slice(2)}`
}

// Diferencia en meses entre dos YYYY-MM
function monthDiff(ymA, ymB) {
  const [yA, mA] = ymA.split('-').map(Number)
  const [yB, mB] = ymB.split('-').map(Number)
  return (yB - yA) * 12 + (mB - mA)
}

export default function CohortLtv() {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [clientes, setClientes] = useState([])
  const [pagos, setPagos] = useState([])

  useEffect(() => { cargar() }, [])

  async function cargar() {
    setLoading(true)
    setError(null)
    try {
      const [{ data: cl, error: ce }, { data: pa, error: pe }] = await Promise.all([
        supabase.from('clientes').select(`
          id, fecha_inicio, estado, cancelled_at, created_at, kit_actual,
          titular:profiles!clientes_titular_fkey(nombre, email)
        `),
        supabase.from('pagos').select('cliente_id, monto, pagado_at').eq('estado', 'pagado'),
      ])
      if (ce) throw ce
      if (pe) throw pe
      setClientes(cl || [])
      setPagos(pa || [])
    } catch (e) {
      setError(e.message || String(e))
    } finally {
      setLoading(false)
    }
  }

  // ── Cohort matrix
  const cohortes = useMemo(() => {
    const map = new Map()  // cohortYM → { ym, total, retM1, retM3, retM6, ltv }
    const hoyYM = ymKey(new Date())

    clientes.forEach(c => {
      if (!c.fecha_inicio) return
      const ym = ymKey(c.fecha_inicio)
      if (!map.has(ym)) map.set(ym, { ym, total: 0, activos_actual: 0, retM1: 0, retM3: 0, retM6: 0, ltv: 0, n_pagos: 0 })
      const r = map.get(ym)
      r.total += 1
      if (c.estado === 'activa') r.activos_actual += 1

      // Retención: si activo/pausado sigue hasta hoy. Si cancelado, usar cancelled_at real.
      const cancelYM = c.estado === 'cancelada' && c.cancelled_at ? ymKey(c.cancelled_at) : hoyYM
      const mesesActivo = monthDiff(ym, cancelYM)
      if (mesesActivo >= 1) r.retM1 += 1
      if (mesesActivo >= 3) r.retM3 += 1
      if (mesesActivo >= 6) r.retM6 += 1
    })

    // LTV por cohorte
    const pagosPorCliente = new Map()
    pagos.forEach(p => {
      pagosPorCliente.set(p.cliente_id, (pagosPorCliente.get(p.cliente_id) || 0) + Number(p.monto || 0))
    })

    clientes.forEach(c => {
      if (!c.fecha_inicio) return
      const ym = ymKey(c.fecha_inicio)
      const r = map.get(ym)
      const total = pagosPorCliente.get(c.id) || 0
      r.ltv += total
      if (total > 0) r.n_pagos += 1
    })

    map.forEach(r => {
      r.ltv_prom = r.total > 0 ? r.ltv / r.total : 0
      r.retM1_pct = r.total > 0 ? (r.retM1 / r.total) * 100 : 0
      r.retM3_pct = r.total > 0 ? (r.retM3 / r.total) * 100 : 0
      r.retM6_pct = r.total > 0 ? (r.retM6 / r.total) * 100 : 0
    })

    return Array.from(map.values()).sort((a, b) => b.ym.localeCompare(a.ym))
  }, [clientes, pagos])

  // ── Top clientes por LTV
  const topClientes = useMemo(() => {
    const map = new Map()
    pagos.forEach(p => {
      if (!p.cliente_id) return
      map.set(p.cliente_id, (map.get(p.cliente_id) || 0) + Number(p.monto || 0))
    })
    const arr = []
    clientes.forEach(c => {
      const total = map.get(c.id) || 0
      if (total > 0) arr.push({
        id: c.id,
        nombre: c.titular?.nombre || '—',
        email: c.titular?.email,
        estado: c.estado,
        fecha_inicio: c.fecha_inicio,
        total,
      })
    })
    arr.sort((a, b) => b.total - a.total)
    return arr.slice(0, 20)
  }, [clientes, pagos])

  const totalGeneral = useMemo(() => {
    const ltv = pagos.reduce((s, p) => s + Number(p.monto || 0), 0)
    const clientesUnicos = new Set(pagos.map(p => p.cliente_id)).size
    return {
      ltv_total: ltv,
      ltv_prom: clientesUnicos > 0 ? ltv / clientesUnicos : 0,
      n_clientes: clientesUnicos,
    }
  }, [pagos])

  return (
    <AdminLayout>
      <div className="page">
        <h1>👥 Cohort + LTV</h1>
        <p className="muted">Retención por cohorte de alta y valor de vida del cliente. Saber si los clientes se quedan o se van con el tiempo.</p>

        <div className="cards-grid" style={{ marginBottom: 16 }}>
          <div className="card">
            <h3>LTV total</h3>
            <div className="stat" style={{ color: '#10B981' }}>{fmtUYU(totalGeneral.ltv_total)}</div>
            <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>todos los pagos cobrados</div>
          </div>
          <div className="card">
            <h3>LTV promedio</h3>
            <div className="stat" style={{ color: '#1a3461' }}>{fmtUYU(totalGeneral.ltv_prom)}</div>
            <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>por cliente</div>
          </div>
          <div className="card">
            <h3>Clientes con pagos</h3>
            <div className="stat">{totalGeneral.n_clientes}</div>
            <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>al menos 1 cobro</div>
          </div>
          <div className="card">
            <h3>Cohortes analizadas</h3>
            <div className="stat">{cohortes.length}</div>
            <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>meses con altas</div>
          </div>
        </div>

        {error && <div style={{ padding: 12, background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 8, color: '#991b1b', marginBottom: 12 }}>{error}</div>}
        {loading && <div className="ap-loading">Cargando…</div>}

        {!loading && cohortes.length > 0 && (
          <>
            <h3 style={{ color: '#1a3461', margin: '24px 0 12px' }}>Cohortes por mes de alta</h3>
            <div style={{ overflowX: 'auto', background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', marginBottom: 24 }}>
              <table className="usu-table">
                <thead>
                  <tr>
                    <th>Cohorte (mes alta)</th>
                    <th style={{ textAlign: 'center' }}>Tamaño</th>
                    <th style={{ textAlign: 'center' }}>Activos hoy</th>
                    <th style={{ textAlign: 'center' }}>Retención M+1</th>
                    <th style={{ textAlign: 'center' }}>Retención M+3</th>
                    <th style={{ textAlign: 'center' }}>Retención M+6</th>
                    <th style={{ textAlign: 'right' }}>LTV total</th>
                    <th style={{ textAlign: 'right' }}>LTV promedio</th>
                  </tr>
                </thead>
                <tbody>
                  {cohortes.map(r => (
                    <tr key={r.ym}>
                      <td style={{ fontWeight: 700, color: '#1a3461' }}>{ymLabel(r.ym)}</td>
                      <td style={{ textAlign: 'center', fontWeight: 700 }}>{r.total}</td>
                      <td style={{ textAlign: 'center', fontWeight: 700, color: '#10B981' }}>{r.activos_actual}</td>
                      <td style={{ textAlign: 'center' }}>
                        <PctBadge value={r.retM1_pct} />
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <PctBadge value={r.retM3_pct} />
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <PctBadge value={r.retM6_pct} />
                      </td>
                      <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>{fmtUYU(r.ltv)}</td>
                      <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: '#1a3461' }}>{fmtUYU(r.ltv_prom)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {!loading && topClientes.length > 0 && (
          <>
            <h3 style={{ color: '#1a3461', margin: '24px 0 12px' }}>🏆 Top 20 clientes por LTV</h3>
            <div style={{ overflowX: 'auto', background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb' }}>
              <table className="usu-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Cliente</th>
                    <th>Estado</th>
                    <th>Alta</th>
                    <th style={{ textAlign: 'right' }}>LTV acumulado</th>
                  </tr>
                </thead>
                <tbody>
                  {topClientes.map((c, i) => (
                    <tr key={c.id}>
                      <td style={{ fontWeight: 800, color: '#1a3461' }}>#{i + 1}</td>
                      <td>
                        <div style={{ fontWeight: 700, color: '#1a3461' }}>{c.nombre}</div>
                        {c.email && <div style={{ fontSize: 11, color: '#94a3b8' }}>{c.email}</div>}
                      </td>
                      <td>
                        <span style={{
                          padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 700,
                          background: c.estado === 'activa' ? '#d1fae5' : c.estado === 'pausada' ? '#fef3c7' : '#fee2e2',
                          color: c.estado === 'activa' ? '#065f46' : c.estado === 'pausada' ? '#92400e' : '#991b1b',
                        }}>
                          {c.estado}
                        </span>
                      </td>
                      <td style={{ fontSize: 12, color: '#6b7280' }}>{c.fecha_inicio || '—'}</td>
                      <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 800, color: '#10B981' }}>{fmtUYU(c.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        <AnalisisIA
          reportType="cohort_ltv"
          data={{ totales: totalGeneral, cohortes: cohortes.slice(0, 12), top_clientes: topClientes.slice(0, 10) }}
        />

        <div style={{ marginTop: 20, padding: 14, background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 10, fontSize: 12, color: '#78350f' }}>
          <strong>📌 Notas:</strong> Retención M+N = % de la cohorte que sigue activo N meses después del alta (calculado con <code>cancelled_at</code> real).
          Benchmarks B2C: M+6 &gt; 70% es excelente.
        </div>
      </div>

      <style>{`
        .usu-table { width: 100%; border-collapse: collapse; font-size: 14px; }
        .usu-table thead th { text-align: left; padding: 12px 14px; background: #f8fafc; border-bottom: 1px solid #e5e7eb; font-size: 12px; text-transform: uppercase; color: #6b7280; font-weight: 700; letter-spacing: .03em; }
        .usu-table tbody td { padding: 12px 14px; border-bottom: 1px solid #f1f5f9; }
        .usu-table tbody tr:hover { background: #f8fafc; }
        .ap-loading { padding: 40px; text-align: center; color: #6b7280; }
        .ap-empty { padding: 60px 20px; text-align: center; color: #6b7280; }
      `}</style>
    </AdminLayout>
  )
}

function PctBadge({ value }) {
  const v = Number(value || 0)
  const color = v >= 70 ? '#065f46' : v >= 40 ? '#92400e' : '#991b1b'
  const bg = v >= 70 ? '#d1fae5' : v >= 40 ? '#fef3c7' : '#fee2e2'
  return (
    <span style={{ padding: '2px 8px', borderRadius: 999, fontSize: 12, fontWeight: 700, background: bg, color }}>
      {v.toFixed(0)}%
    </span>
  )
}
